import { useEffect, useMemo, useState } from 'react'
import {
    MdChevronRight,
    MdClose,
    MdDriveFileMove,
    MdFolder,
    MdHome,
    MdLock,
    MdSearch,
} from 'react-icons/md'
import { getFolderTree, type StorageFolder } from '@/lib/api/storage/storage-api'

interface Props {
    isOpen: boolean
    /** Arquivos que serão movidos. */
    fileCount: number
    /** Pastas que serão movidas. Elas e suas descendentes não podem ser destino. */
    movingFolderIds: number[]
    /**
     * Pasta de origem, quando todos os itens vêm da mesma: o destino já começa
     * nela e mover para ela fica desabilitado. `undefined` na busca global, em
     * que os itens podem vir de pastas diferentes.
     */
    currentFolderId?: number | null
    moving: boolean
    onClose: () => void
    onConfirm: (targetFolderId: number | null) => void
}

/** Rótulo do que está sendo movido, para a tela não dizer "1 itens". */
function describeSelection(fileCount: number, folderCount: number): string {
    const parts: string[] = []
    if (fileCount) parts.push(`${fileCount} ${fileCount === 1 ? 'arquivo' : 'arquivos'}`)
    if (folderCount) parts.push(`${folderCount} ${folderCount === 1 ? 'pasta' : 'pastas'}`)
    return parts.join(' e ')
}

/**
 * Escolha da pasta de destino para mover arquivos e pastas.
 *
 * A árvore inteira vem de uma só chamada (`storage/folders/tree`) e é exibida
 * indentada pela profundidade. Navegar pasta a pasta obrigaria a uma ida ao
 * servidor por nível — e quem precisa mover normalmente já sabe o nome do
 * destino, por isso a busca filtra a lista toda de uma vez.
 */
export default function StorageMoveModal({
    isOpen,
    fileCount,
    movingFolderIds,
    currentFolderId,
    moving,
    onClose,
    onConfirm,
}: Props) {
    const [folders, setFolders] = useState<StorageFolder[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [target, setTarget] = useState<number | null>(null)

    useEffect(() => {
        if (!isOpen) return

        setSearch('')
        setTarget(currentFolderId ?? null)
        setLoading(true)
        void (async () => {
            const response = await getFolderTree()
            setFolders(response.success ? (response.result ?? []) : [])
            setLoading(false)
        })()
    }, [isOpen, currentFolderId])

    /**
     * Destinos proibidos: as pastas selecionadas e tudo o que está dentro
     * delas. Sem isso a tela ofereceria mover uma pasta para dentro de si
     * mesma, e o backend responderia com erro depois do clique.
     */
    const blockedIds = useMemo(() => {
        const blocked = new Set(movingFolderIds)
        if (!blocked.size) return blocked

        // A árvore vem ordenada por profundidade, então um único passe basta
        // para propagar o bloqueio do pai para os filhos.
        for (const folder of folders) {
            if (folder.parentId != null && blocked.has(folder.parentId)) {
                blocked.add(folder.id)
            }
        }
        return blocked
    }, [folders, movingFolderIds])

    /**
     * Achata a árvore em ordem de leitura (pai seguido dos filhos), com o nível
     * de cada pasta para a indentação. O nível é calculado aqui porque a API
     * devolve só `parentId` — e uma pasta cujo pai o usuário não enxerga (por
     * restrição de perfil) entra como raiz, senão sumiria da lista.
     */
    const orderedFolders = useMemo(() => {
        const byParent = new Map<number | null, StorageFolder[]>()
        const ids = new Set(folders.map(folder => folder.id))

        for (const folder of folders) {
            const parent =
                folder.parentId != null && ids.has(folder.parentId)
                    ? folder.parentId
                    : null
            const siblings = byParent.get(parent)
            if (siblings) siblings.push(folder)
            else byParent.set(parent, [folder])
        }

        const flat: Array<{ folder: StorageFolder; level: number }> = []
        const walk = (parent: number | null, level: number) => {
            const children = byParent.get(parent) ?? []
            for (const folder of [...children].sort((a, b) =>
                a.name.localeCompare(b.name, 'pt-BR'),
            )) {
                flat.push({ folder, level })
                walk(folder.id, level + 1)
            }
        }
        walk(null, 0)
        return flat
    }, [folders])

    const visibleFolders = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return orderedFolders
        return orderedFolders.filter(({ folder }) =>
            folder.name.toLowerCase().includes(term),
        )
    }, [orderedFolders, search])

    const targetName = useMemo(() => {
        if (target == null) return 'Início'
        return folders.find(folder => folder.id === target)?.name ?? ''
    }, [folders, target])

    if (!isOpen) return null

    const folderCount = movingFolderIds.length
    const unchanged = currentFolderId !== undefined && target === currentFolderId
    const searching = !!search.trim()

    return (
        <div className="modal modal-open">
            <div className="modal-box flex max-h-[85vh] max-w-lg flex-col">
                <div className="mb-1 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                        <MdDriveFileMove className="text-xl text-primary" />
                        Mover para
                    </h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                        disabled={moving}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <p className="mb-4 text-sm text-base-content/60">
                    {describeSelection(fileCount, folderCount)} a mover. Escolha a pasta
                    de destino.
                </p>

                <div className="relative mb-3">
                    {/* z-10: sem isso o ícone fica atrás do fundo do input. */}
                    <MdSearch className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg text-base-content/40" />
                    <input
                        type="search"
                        className="input input-bordered input-sm w-full pl-9"
                        placeholder="Buscar pasta pelo nome"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        disabled={moving}
                    />
                </div>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <span className="loading loading-spinner loading-md text-primary" />
                    </div>
                ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-base-300 p-1.5">
                        {/* A raiz não é uma pasta no banco, mas é destino válido:
                            é para onde vai quem foi enviado "na tela inicial". */}
                        {!searching && (
                            <button
                                type="button"
                                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                                    target === null
                                        ? 'bg-primary/10 font-medium text-primary'
                                        : 'hover:bg-base-200'
                                }`}
                                onClick={() => setTarget(null)}
                                disabled={moving}
                            >
                                <MdHome className="shrink-0 text-lg" />
                                <span className="truncate">Início</span>
                                {currentFolderId === null && (
                                    <span className="ml-auto shrink-0 text-xs text-base-content/40">
                                        Atual
                                    </span>
                                )}
                            </button>
                        )}

                        {visibleFolders.map(({ folder, level }) => {
                            const blocked = blockedIds.has(folder.id)
                            const selected = target === folder.id
                            return (
                                <button
                                    key={folder.id}
                                    type="button"
                                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                                        blocked
                                            ? 'cursor-not-allowed text-base-content/30'
                                            : selected
                                              ? 'bg-primary/10 font-medium text-primary'
                                              : 'hover:bg-base-200'
                                    }`}
                                    style={
                                        searching
                                            ? undefined
                                            : { paddingLeft: `${0.625 + level * 1.1}rem` }
                                    }
                                    onClick={() => !blocked && setTarget(folder.id)}
                                    disabled={moving || blocked}
                                    title={
                                        blocked
                                            ? 'Uma pasta não pode ser movida para dentro dela mesma'
                                            : folder.name
                                    }
                                >
                                    {!searching && level > 0 && (
                                        <MdChevronRight className="shrink-0 text-base text-base-content/25" />
                                    )}
                                    <MdFolder className="shrink-0 text-lg text-warning" />
                                    <span className="truncate">{folder.name}</span>
                                    {folder.hasPassword && (
                                        <MdLock
                                            className="shrink-0 text-warning"
                                            title="Protegida por senha"
                                        />
                                    )}
                                    {currentFolderId === folder.id && (
                                        <span className="ml-auto shrink-0 text-xs text-base-content/40">
                                            Atual
                                        </span>
                                    )}
                                </button>
                            )
                        })}

                        {!visibleFolders.length && (
                            <p className="px-2.5 py-6 text-center text-sm text-base-content/50">
                                {searching
                                    ? 'Nenhuma pasta com esse nome.'
                                    : 'Nenhuma pasta criada ainda. Crie uma pasta para organizar os arquivos.'}
                            </p>
                        )}
                    </div>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={moving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => onConfirm(target)}
                        disabled={moving || loading || unchanged}
                        title={
                            unchanged
                                ? 'Os itens já estão nesta pasta'
                                : `Mover para ${targetName}`
                        }
                    >
                        {moving && <span className="loading loading-spinner loading-sm" />}
                        Mover para {targetName || 'a pasta'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !moving && onClose()} />
        </div>
    )
}
